import { useState } from 'react';
import {
  ChartFrame,
  EmptyChart,
  INK,
  Legend,
  MotionStyles,
  XAxis,
  seriesColour,
  useMeasuredWidth,
  usePrefersReducedMotion,
  type ChartTable,
  type TooltipState,
} from './Chart';
import { bandScale, linePath, linearScale, niceDomain, niceTicks } from './layout';

/**
 * Lines over any run of points - weeks, months - with an optional shaded
 * band between two of them, and values that need not be money.
 *
 * ── WHY NOT LineChart ──
 * `LineChart` is the months-and-money chart: it labels its axis with month
 * names and every value with a currency. The CFO view needs two things it
 * cannot say. A thirteen-week cash forecast, where the points are dates and
 * the band between "expected" and "cautious" IS the message - the width of
 * the band is the risk the late invoices carry. And "days to get paid",
 * where the value is days, not rupees, and a month with no payments is a
 * gap in the line rather than a zero that would read as "paid instantly".
 *
 * So the caller hands over the labels and the formatters. Everything else
 * - the frame, the tooltip, keyboard stepping, the hidden table - is the
 * kit's, read the same way as every other chart.
 */

export interface SeriesLine {
  id: string;
  name: string;
  /** A palette token, null for the theme's primary. */
  colour: string | null;
  /** A null is a gap: no value that point, not a zero. */
  values: Array<number | null>;
  dashed?: boolean;
}

export interface SeriesChartProps {
  /** The axis label for each point: "13 Oct", "Apr". */
  labels: string[];
  /** The tooltip and table label for each point: "Monday 13 October 2026". Defaults to `labels`. */
  longLabels?: string[];
  lines: SeriesLine[];
  /** Shade between these two lines. */
  band?: { from: string; to: string; colour: string | null; label: string };
  /** A value as the tooltip and table say it. */
  format: (value: number) => string;
  /** A value as an axis tick says it: shorter. */
  tick: (value: number) => string;
  title: string;
  summary: string;
  empty: { title: string; description?: string };
  height?: number;
  /** Keep zero on the axis, so a line crossing it is seen to. Defaults to true. */
  includeZero?: boolean;
}

const MARGIN = { top: 14, right: 16, bottom: 26 };

export function SeriesChart({ labels, longLabels, lines, band, format, tick, title, summary, empty, height = 240, includeZero = true }: SeriesChartProps) {
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState<number | null>(null);

  const values = lines.flatMap((line) => line.values.filter((v): v is number => v !== null));
  if (labels.length === 0 || values.length === 0) return <EmptyChart title={empty.title} description={empty.description} />;

  const lo = Math.min(includeZero ? 0 : Infinity, ...values);
  const hi = Math.max(includeZero ? 0 : -Infinity, ...values);
  const domain = niceDomain(lo, hi === lo ? lo + 1 : hi);
  const ticks = niceTicks(lo, hi === lo ? lo + 1 : hi);
  const plotLeft = Math.round(Math.max(1, ...ticks.map((t) => tick(t).length)) * 6.5) + 10;
  const plotRight = Math.max(plotLeft + 40, width - MARGIN.right);
  const plotTop = MARGIN.top;
  const plotBottom = height - MARGIN.bottom;
  const y = linearScale(domain, [plotBottom, plotTop]);
  const slots = bandScale(labels.length, plotLeft, plotRight, 0, 0.5);
  const centre = (i: number) => slots.x(i) + slots.band / 2;
  const long = longLabels ?? labels;

  const pointsOf = (line: SeriesLine) => line.values.map((v, i) => (v === null ? null : { x: centre(i), y: y(v) }));

  const bandPath = (() => {
    if (!band) return null;
    const upper = lines.find((l) => l.id === band.from);
    const lower = lines.find((l) => l.id === band.to);
    if (!upper || !lower) return null;
    const top = upper.values.map((v, i) => (v === null ? null : `${centre(i).toFixed(1)} ${y(v).toFixed(1)}`)).filter(Boolean);
    const bottom = lower.values
      .map((v, i) => (v === null ? null : `${centre(i).toFixed(1)} ${y(v).toFixed(1)}`))
      .filter(Boolean)
      .reverse();
    if (top.length < 2 || bottom.length < 2) return null;
    return `M ${top.join(' L ')} L ${bottom.join(' L ')} Z`;
  })();

  const tooltipFor = (i: number): TooltipState => ({
    x: centre(i),
    y: Math.min(...lines.map((line) => (line.values[i] === null || line.values[i] === undefined ? plotBottom : y(line.values[i] as number)))),
    title: long[i] ?? '',
    rows: lines.map((line) => ({
      key: line.id,
      colour: line.colour,
      kind: 'line' as const,
      label: line.name,
      value: line.values[i] === null || line.values[i] === undefined ? 'none' : format(line.values[i] as number),
    })),
  });

  const step = (direction: -1 | 1 | 'first' | 'last') =>
    setActive((current) => {
      if (direction === 'first') return 0;
      if (direction === 'last') return labels.length - 1;
      const next = (current ?? (direction === 1 ? -1 : labels.length)) + direction;
      return Math.max(0, Math.min(labels.length - 1, next));
    });

  const table: ChartTable = {
    caption: title,
    columns: ['When', ...lines.map((l) => l.name)],
    rows: labels.map((label, i) => ({
      key: `${label}-${i}`,
      cells: [long[i] ?? label, ...lines.map((l) => (l.values[i] === null || l.values[i] === undefined ? 'none' : format(l.values[i] as number)))],
    })),
  };

  const pointer = (event: React.PointerEvent<SVGSVGElement>) => slots.index(event.clientX - event.currentTarget.getBoundingClientRect().left);

  return (
    <div className="space-y-2">
      <ChartFrame title={title} table={table} tooltip={active === null ? null : tooltipFor(active)} onStep={step} onLeave={() => setActive(null)} frameRef={frameRef}>
        <svg
          role="img"
          aria-label={summary}
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block select-none"
          style={{ touchAction: 'pan-y' }}
          onPointerMove={(event) => {
            if (event.pointerType !== 'touch') setActive(pointer(event));
          }}
          onPointerDown={(event) => {
            if (event.pointerType !== 'touch') return;
            const i = pointer(event);
            setActive((current) => (current === i ? null : i));
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== 'touch') setActive(null);
          }}
        >
          <defs>
            <MotionStyles enabled={!reduced} />
          </defs>
          <g aria-hidden>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={plotLeft} x2={plotRight} y1={y(t)} y2={y(t)} stroke={t === 0 ? INK.zero : INK.grid} strokeWidth={1} shapeRendering="crispEdges" />
                <text x={plotLeft - 6} y={y(t)} dy="0.35em" textAnchor="end" fontSize={11} fill={INK.axis} className="tabular-nums">
                  {tick(t)}
                </text>
              </g>
            ))}
          </g>
          {bandPath && band ? <path aria-hidden d={bandPath} fill={seriesColour(band.colour)} opacity={0.14} /> : null}
          {active !== null ? (
            <line aria-hidden x1={centre(active)} x2={centre(active)} y1={plotTop} y2={plotBottom} stroke={INK.zero} strokeWidth={1} shapeRendering="crispEdges" />
          ) : null}
          {lines.map((line) => {
            const points = pointsOf(line);
            const ink = seriesColour(line.colour);
            return (
              <g key={line.id} style={reduced ? undefined : { animation: 'chart-fade 500ms ease-out both' }}>
                <path
                  d={linePath(points)}
                  fill="none"
                  stroke={ink}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeDasharray={line.dashed ? '5 4' : undefined}
                >
                  <title>{line.name}</title>
                </path>
                {points.map((point, i) =>
                  point && (active === i || points.filter(Boolean).length === 1) ? (
                    <circle key={i} cx={point.x} cy={point.y} r={4} fill={ink} stroke={INK.surface} strokeWidth={2} />
                  ) : null,
                )}
              </g>
            );
          })}
          <XAxis labels={labels} centre={centre} y={height - 8} band={slots.step} />
        </svg>
      </ChartFrame>
      <Legend
        items={[
          ...lines.map((line) => ({ key: line.id, label: line.name, colour: line.colour, kind: 'line' as const })),
          ...(band ? [{ key: 'band', label: band.label, colour: band.colour, kind: 'tint' as const }] : []),
        ]}
      />
    </div>
  );
}
